import { useState, useMemo, useEffect } from "react";
import { DropZone } from "./components/DropZone";
import { Summary } from "./components/Summary";
import { Feeds } from "./components/Feeds";
import { RunList } from "./components/RunList";
import { SourcesColumn } from "./components/SourcesColumn";
import { PackagesColumn } from "./components/PackagesColumn";
import { loadBundle, sortedRuns, isMeaningfulRun, type SortDir } from "./lib/bundle";
import { matchesQuery } from "./lib/search";
import { useUrlState } from "./lib/url-state";
import { buildSources } from "./lib/sources";
import type { BundleIndex } from "./lib/types";

export function App() {
  const [index, setIndex] = useState<BundleIndex | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // URL-synced state — gets persisted in `?` params for shareable links.
  // Defaults: no query, empty runs SHOWN (so you don't miss anything by
  // accident); user can flip the "hide empty" filter explicitly.
  const [query, setQuery] = useUrlState("q", "");
  const [selectedRunId, setSelectedRunId] = useUrlState("run", "");
  const [selectedSourceId, setSelectedSourceId] = useUrlState("source", "");
  const [hideEmptyRaw, setHideEmptyRaw] = useUrlState("hideEmpty", "");
  const hideEmpty = hideEmptyRaw === "1";
  const setHideEmpty = (v: boolean) => setHideEmptyRaw(v ? "1" : "");
  // Action filter — when set, the run list is filtered to only runs
  // whose counts include at least one decision with that action.
  // Empty string = no filter (show all). Persists in URL so a chip-clicked
  // view is shareable.
  const [actionFilter, setActionFilter] = useUrlState("action", "");

  const [sortDir, setSortDir] = useState<SortDir>("desc"); // "latest first" by default

  async function handleFile(file: File) {
    setLoading(true);
    setError(null);
    try {
      const { index: idx } = await loadBundle(file);
      idx.runs = sortedRuns(idx.runs, "desc");
      setIndex(idx);
      setSortDir("desc");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  // Compute per-run match counts whenever the query changes.
  const matchCounts = useMemo(() => {
    const m = new Map<string, number>();
    if (!index) return m;
    for (const r of index.runs) {
      const res = matchesQuery(r, query);
      m.set(r.runId, query.trim() ? res.count : 0);
    }
    return m;
  }, [index, query]);

  // Apply (in order): hide-empty filter → action filter → search filter → sort.
  // actionFilter shows only runs whose decisions include at least one entry
  // with that action. counts[action] > 0 is the cheap pre-computed check.
  const displayedRuns = useMemo(() => {
    if (!index) return [];
    let runs = index.runs;
    if (hideEmpty) runs = runs.filter(isMeaningfulRun);
    if (actionFilter) {
      const key = actionFilter as keyof typeof runs[number]["counts"];
      runs = runs.filter((r) => (r.counts[key] ?? 0) > 0);
    }
    if (query.trim()) runs = runs.filter((r) => matchesQuery(r, query).matched);
    return sortedRuns(runs, sortDir);
  }, [index, query, hideEmpty, actionFilter, sortDir]);

  // Auto-select first matching run if current selection is gone. Doing
  // this in an effect so URL state stays consistent with what's visible.
  useEffect(() => {
    if (!index) return;
    if (selectedRunId && displayedRuns.some((r) => r.runId === selectedRunId)) return;
    const next = displayedRuns[0]?.runId ?? "";
    if (next !== selectedRunId) setSelectedRunId(next);
  }, [index, displayedRuns, selectedRunId, setSelectedRunId]);

  const selectedRun = useMemo(() => {
    if (!index || !selectedRunId) return null;
    return index.runs.find((r) => r.runId === selectedRunId) ?? null;
  }, [index, selectedRunId]);

  // Derive the source list for the selected run + the selected source
  // object. Defaults to the pinned "All packages" entry when the user
  // hasn't picked one (or when the previous selection is gone after a
  // run change).
  const sourcesForRun = useMemo(
    () => (selectedRun ? buildSources(selectedRun) : []),
    [selectedRun],
  );
  const selectedSource = useMemo(() => {
    if (sourcesForRun.length === 0) return null;
    const match = sourcesForRun.find((s) => s.id === selectedSourceId);
    if (match) return match;
    return sourcesForRun[0];
  }, [sourcesForRun, selectedSourceId]);

  // Auto-select the first source whenever the run changes or the previous
  // source selection is no longer valid for this run.
  useEffect(() => {
    if (sourcesForRun.length === 0) {
      if (selectedSourceId !== "") setSelectedSourceId("");
      return;
    }
    if (selectedSourceId && sourcesForRun.some((s) => s.id === selectedSourceId)) return;
    const next = sourcesForRun[0]?.id ?? "";
    if (next !== selectedSourceId) setSelectedSourceId(next);
  }, [sourcesForRun, selectedSourceId, setSelectedSourceId]);

  // Counts shown next to the search input (across the FULL run list, not
  // filtered by hide-empty — that filter is independent of search).
  const totalMatchCount = useMemo(() => {
    let total = 0;
    for (const c of matchCounts.values()) total += c;
    return total;
  }, [matchCounts]);
  const matchedRunCount = query.trim()
    ? displayedRuns.length
    : (index?.runs.length ?? 0);

  const emptyRunCount = useMemo(
    () => (index ? index.runs.filter((r) => !isMeaningfulRun(r)).length : 0),
    [index],
  );

  function reset() {
    setIndex(null);
    setSelectedRunId("");
    setSelectedSourceId("");
    setQuery("");
    setHideEmpty(false);
    setActionFilter("");
    setError(null);
  }

  // Jump the run column to the run that processed a blog post (from the Feeds
  // panel). Clear the list filters first so the target run is guaranteed to be
  // in the visible list — otherwise the auto-select effect would immediately
  // bounce the selection back to the first visible run.
  function selectRunFromFeed(runId: string) {
    setQuery("");
    setActionFilter("");
    setHideEmpty(false);
    setSelectedSourceId("");
    setSelectedRunId(runId);
  }

  if (!index) {
    return <DropZone onFile={handleFile} loading={loading} error={error} />;
  }

  return (
    <div className="app">
      <Summary
        index={index}
        onReset={reset}
        query={query}
        onQueryChange={setQuery}
        matchedRunCount={matchedRunCount}
        totalMatchCount={totalMatchCount}
        hideEmpty={hideEmpty}
        onToggleHideEmpty={() => setHideEmpty(!hideEmpty)}
        emptyRunCount={emptyRunCount}
        actionFilter={actionFilter}
        onSelectAction={(a) =>
          // Toggle: click an active chip to clear; otherwise set it.
          setActionFilter(actionFilter === a ? "" : a)
        }
        filteredRunCount={displayedRuns.length}
      />
      <Feeds index={index} onSelectRun={selectRunFromFeed} />
      <div className="app__body app__body--3col">
        <RunList
          runs={displayedRuns}
          selectedRunId={selectedRunId || null}
          onSelect={(id) => {
            setSelectedRunId(id);
            setSelectedSourceId(""); // reset, auto-effect will pick the first source
          }}
          sortDir={sortDir}
          onChangeSortDir={setSortDir}
          query={query}
          matchCounts={matchCounts}
          totalAvailableRuns={index.runs.length}
        />
        {selectedRun ? (
          <>
            <SourcesColumn
              run={selectedRun}
              selectedSourceId={selectedSource?.id ?? ""}
              onSelectSource={setSelectedSourceId}
              query={query}
            />
            <PackagesColumn
              run={selectedRun}
              source={selectedSource}
              query={query}
              initialActionFilter={actionFilter}
            />
          </>
        ) : (
          <main className="packages packages--empty">
            <p className="packages__empty-msg">
              {query.trim() ? `No runs match "${query}".` : "Pick a run from the left to see its sources."}
            </p>
          </main>
        )}
      </div>
    </div>
  );
}
